import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sessionsAPI, translateAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mic, MicOff, PhoneOff, UserPlus, Eye, EyeOff, AlertTriangle, Shield, Star, Send, Loader2, ArrowLeft, Volume2, Phone, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';

const LANG_NAMES = {
  en: 'English', es: 'Spanish', zh: 'Mandarin', ar: 'Arabic',
  vi: 'Vietnamese', fr: 'French', tl: 'Tagalog', ko: 'Korean',
  ru: 'Russian', pt: 'Portuguese', hi: 'Hindi'
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SessionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [inputText, setInputText] = useState('');
  const [translating, setTranslating] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [endSummary, setEndSummary] = useState(null);
  const [aiRating, setAiRating] = useState(0);
  const [humanRating, setHumanRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [micStatus, setMicStatus] = useState('idle'); // idle, requesting, recording, transcribing, error
  const [micPermission, setMicPermission] = useState('unknown'); // unknown, granted, denied, prompt
  const [micError, setMicError] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [textDirection, setTextDirection] = useState('forward'); // forward = source→target, reverse = target→source
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const loadSession = useCallback(async () => {
    try {
      const res = await sessionsAPI.get(id);
      setSession(res.data);
      if (res.data.advisory_state === 'HIGH_RISK') setShowRiskDialog(true);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    loadSession();
    const interval = setInterval(loadSession, 5000);
    return () => clearInterval(interval);
  }, [loadSession]);

  // Scroll to bottom when transcript updates
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [session?.transcript?.length]);

  // Cleanup media stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Check microphone permission on mount
  useEffect(() => {
    const checkMic = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setMicPermission(result.state); // 'granted', 'denied', 'prompt'
          result.addEventListener('change', () => setMicPermission(result.state));
        }
      } catch {
        // permissions API not supported — leave as unknown
      }
    };
    checkMic();
  }, []);

  // ── Audio Recording (click-to-toggle) ──
  const startRecording = async () => {
    // Check secure context and mediaDevices availability
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setMicStatus('error');
      setMicPermission('denied');
      setMicError('navigator.mediaDevices is not available');
      return;
    }
    try {
      setMicStatus('requesting');
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true }
        });
      } catch (constraintErr) {
        // Fallback: try with minimal audio constraints
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
      audioChunksRef.current = [];

      // Pick a supported MIME type
      let mimeType = 'audio/webm;codecs=opus';
      if (typeof MediaRecorder !== 'undefined') {
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
                     MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
        }
      }
      const opts = mimeType ? { mimeType } : undefined;
      const mediaRecorder = new MediaRecorder(stream, opts);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        const ext = (mediaRecorder.mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        if (blob.size < 100) {
          setMicStatus('idle');
          toast && toast.info('Recording too short — speak and then click stop');
          return;
        }
        await processAudio(blob, ext);
      };

      mediaRecorder.start(250);
      setIsRecording(true);
      setMicStatus('recording');
      setMicPermission('granted');
    } catch (e) {
      console.error('Mic access error:', e.name, e.message);
      setMicStatus('error');
      setMicError(`${e.name || 'Error'}: ${e.message || 'Unknown'}`);
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        setMicPermission('denied');
      } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
        toast.error('Microphone is in use by another application. Please close other apps using the microphone and try again.');
      } else if (e.name === 'TypeError') {
        setMicPermission('denied');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setMicStatus('transcribing');
  };

  const processAudio = async (blob, ext = 'webm') => {
    // Step 1: Transcribe audio with auto language detection
    try {
      setMicStatus('transcribing');
      const formData = new FormData();
      formData.append('file', blob, `recording.${ext}`);
      // Don't pass language — let Whisper auto-detect
      formData.append('language', '');

      const token = localStorage.getItem('token');
      const transcribeRes = await fetch(`${API_BASE}/api/speech/transcribe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!transcribeRes.ok) {
        const err = await transcribeRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Transcription failed');
      }

      const transcription = await transcribeRes.json();
      if (!transcription.text?.trim()) {
        setMicStatus('idle');
        toast.info('No speech detected — try speaking louder or closer to the mic');
        return;
      }

      // Step 2: Determine translation direction based on detected language
      const detectedLang = transcription.detected_language || transcription.language || session.source_language;
      let srcLang, tgtLang;

      // If the detected language matches the target, reverse direction
      if (detectedLang === session.target_language || detectedLang.startsWith(session.target_language)) {
        srcLang = session.target_language;
        tgtLang = session.source_language;
      } else {
        srcLang = session.source_language;
        tgtLang = session.target_language;
      }

      // Step 3: Translate the transcribed text
      setTranslating(true);
      await translateAPI.translate({
        session_id: id,
        text: transcription.text,
        source_language: srcLang,
        target_language: tgtLang,
        domain_level: session.domain_level,
      });

      await loadSession();
      setMicStatus('idle');
      setTranslating(false);

      // Step 4: Auto-play TTS of the translation
      const updatedSession = (await sessionsAPI.get(id)).data;
      const lastEntry = updatedSession.transcript?.[updatedSession.transcript.length - 1];
      if (lastEntry?.translated) {
        playTTS(lastEntry.translated, tgtLang);
      }
    } catch (e) {
      console.error('Audio processing error:', e);
      setMicStatus('error');
      setMicError(`Processing: ${e.message || 'Audio processing failed'}`);
      setTranslating(false);
    }
  };

  // ── TTS Playback ──
  const playTTS = async (text, language) => {
    try {
      setPlayingAudio(text.slice(0, 20));
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/speech/synthesize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, language }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('TTS failed:', res.status, err.detail || err);
        setPlayingAudio(null);
        return;
      }
      const audioBlob = await res.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.onended = () => { setPlayingAudio(null); URL.revokeObjectURL(audioUrl); };
      audio.onerror = (e) => { console.error('Audio playback error:', e); setPlayingAudio(null); URL.revokeObjectURL(audioUrl); };
      audio.play().catch(e => { console.error('Audio play() rejected:', e); setPlayingAudio(null); });
    } catch (e) {
      console.error('TTS error:', e);
      setPlayingAudio(null);
    }
  };

  // ── Text Translation ──
  const handleTranslate = async () => {
    if (!inputText.trim() || translating) return;
    setTranslating(true);
    const srcLang = textDirection === 'forward' ? session.source_language : session.target_language;
    const tgtLang = textDirection === 'forward' ? session.target_language : session.source_language;
    try {
      await translateAPI.translate({
        session_id: id,
        text: inputText,
        source_language: srcLang,
        target_language: tgtLang,
        domain_level: session.domain_level,
      });
      setInputText('');
      await loadSession();
    } catch (e) { console.error(e); }
    setTranslating(false);
  };

  const handleEscalate = async () => {
    try {
      await sessionsAPI.escalate({ session_id: id, reason: 'User requested human interpreter' });
      await loadSession();
    } catch (e) { console.error(e); }
  };

  const handleAcknowledgeRisk = async () => {
    try {
      await sessionsAPI.acknowledgeRisk({ session_id: id, acknowledged: true });
      setShowRiskDialog(false);
      await loadSession();
    } catch (e) { console.error(e); }
  };

  const handleEndSession = async () => {
    try {
      const res = await sessionsAPI.end(id);
      setEndSummary(res.data);
      setShowEndDialog(false);
      setShowRateDialog(true);
    } catch (e) { console.error(e); }
  };

  const handleRate = async () => {
    try {
      await sessionsAPI.rate({
        session_id: id,
        ai_rating: aiRating || null,
        human_rating: humanRating || null,
        feedback: feedback || null,
      });
      navigate('/client');
    } catch (e) { console.error(e); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTranslate(); }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" />
        </div>
      </AppShell>
    );
  }

  if (!session) return null;
  const transcript = session.transcript || [];
  const isEnded = session.status === 'ended';

  return (
    <AppShell>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="session-view">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between bg-card">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/client')} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-semibold" style={{ fontFamily: 'DM Sans' }}>
                  {LANG_NAMES[session.source_language]} &rarr; {LANG_NAMES[session.target_language]}
                </h2>
                <Badge variant={session.status === 'active' ? 'default' : session.status === 'ended' ? 'secondary' : 'destructive'} className="text-xs">
                  {session.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground font-mono">Domain: {session.domain_level} | AI Seconds: {session.ai_seconds}s</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={showTranscript} onCheckedChange={setShowTranscript} data-testid="transcript-toggle" />
              <Label className="text-xs">{showTranscript ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Label>
            </div>
          </div>
        </div>

        {/* Advisory Banners */}
        {session.advisory_state === 'ADVISORY' && (
          <Alert className="rounded-none border-x-0 border-t-0 bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30" data-testid="advisory-banner">
            <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
            <AlertDescription className="text-sm text-[hsl(var(--warning))]">Advisory: Translation confidence is below optimal. Consider requesting a human interpreter.</AlertDescription>
          </Alert>
        )}
        {session.advisory_state === 'HIGH_RISK' && (
          <Alert className="rounded-none border-x-0 border-t-0 bg-destructive/10 border-destructive/30" data-testid="high-risk-banner">
            <Shield className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-sm text-destructive font-medium">HIGH RISK: Translation accuracy may be insufficient. Acknowledgement required.</AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-0 overflow-hidden">
          {/* Translation Area */}
          <div className="lg:col-span-2 flex flex-col border-r">
            {/* Mic Controls - Always visible at top */}
            {!isEnded && (
              <div className="border-b bg-card shrink-0">
                {/* Auto-detect indicator */}
                <div className="flex items-center justify-center gap-2 pt-4 pb-1">
                  <Badge variant="outline" className="text-xs">{LANG_NAMES[session.source_language] || session.source_language}</Badge>
                  <ArrowLeftRight className="w-3.5 h-3.5 text-muted-foreground" />
                  <Badge variant="outline" className="text-xs">{LANG_NAMES[session.target_language] || session.target_language}</Badge>
                  <span className="text-[10px] text-muted-foreground ml-1">auto-detect</span>
                </div>
                {/* Centered Mic Controls */}
                <div className="flex flex-col items-center justify-center py-3 gap-3" data-testid="mic-controls">
                  {micStatus === 'idle' && !isRecording && (
                    <>
                      <button
                        onClick={startRecording}
                        className="w-20 h-20 rounded-full border-2 border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand))]/10 flex items-center justify-center transition-all duration-200 hover:scale-110 hover:border-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/20 active:scale-95 cursor-pointer"
                        data-testid="start-recording-button"
                      >
                        <Mic className="w-8 h-8 text-[hsl(var(--brand))]" />
                      </button>
                      <span className="text-sm font-medium text-muted-foreground">Click to Start</span>
                      {micPermission === 'denied' && (
                        <div className="flex flex-col items-center gap-1 text-destructive text-xs mt-1" data-testid="mic-denied-warning">
                          <div className="flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            <span>Microphone blocked — click the lock icon in your address bar to allow access, then reload</span>
                          </div>
                          <span className="text-muted-foreground">You can still type text below to translate</span>
                        </div>
                      )}
                      {micPermission === 'prompt' && (
                        <span className="text-xs text-muted-foreground mt-1">Your browser will ask for microphone permission</span>
                      )}
                    </>
                  )}

                  {micStatus === 'requesting' && (
                    <>
                      <div className="w-20 h-20 rounded-full border-2 border-muted-foreground/30 bg-muted/20 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                      <span className="text-sm text-muted-foreground">Requesting microphone access...</span>
                    </>
                  )}

                  {isRecording && micStatus === 'recording' && (
                    <>
                      <div className="w-20 h-20 rounded-full border-2 border-red-500/60 bg-red-500/10 flex items-center justify-center animate-pulse">
                        <Mic className="w-8 h-8 text-red-500" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-sm font-medium text-red-500">Recording...</span>
                      </div>
                      <Button
                        variant="destructive"
                        onClick={stopRecording}
                        className="mt-1 px-6"
                        data-testid="stop-translate-button"
                      >
                        <MicOff className="w-4 h-4 mr-2" />Click to Stop and Translate
                      </Button>
                    </>
                  )}

                  {micStatus === 'transcribing' && (
                    <>
                      <div className="w-20 h-20 rounded-full border-2 border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand))]/10 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" />
                      </div>
                      <span className="text-sm font-medium text-[hsl(var(--brand))]">Processing speech...</span>
                    </>
                  )}

                  {micStatus === 'error' && (
                    <>
                      <div className="w-20 h-20 rounded-full border-2 border-destructive/40 bg-destructive/10 flex items-center justify-center">
                        <AlertTriangle className="w-8 h-8 text-destructive" />
                      </div>
                      <span className="text-sm text-destructive font-medium">Microphone error</span>
                      <div className="text-center max-w-sm">
                        <p className="text-xs text-muted-foreground">Microphone access was denied or unavailable. To fix:</p>
                        <ol className="text-xs text-muted-foreground mt-1 text-left list-decimal list-inside space-y-0.5">
                          <li>Click the icon to the left of the URL in your address bar (sliders icon in Chrome)</li>
                          <li>Find "Microphone" and set it to "Allow"</li>
                          <li>Reload this page</li>
                        </ol>
                        <p className="text-xs text-muted-foreground mt-1.5">Or go to <span className="font-mono text-[10px]">chrome://settings/content/microphone</span> and ensure this site is allowed.</p>
                        <p className="text-xs text-muted-foreground mt-2">You can also use the text input below to type your message instead.</p>
                        {micError && <p className="text-[10px] text-muted-foreground/60 mt-2 font-mono">Error: {micError}</p>}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => { setMicStatus('idle'); setMicError(''); }} className="mt-1">Try Again</Button>
                    </>
                  )}
                </div>

                {/* Text Input with direction toggle */}
                <div className="flex items-center gap-2 px-4 pb-4">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setTextDirection(d => d === 'forward' ? 'reverse' : 'forward')}
                    className="shrink-0 text-xs gap-1"
                    data-testid="text-direction-toggle"
                    title={textDirection === 'forward' ? `${LANG_NAMES[session.source_language]} → ${LANG_NAMES[session.target_language]}` : `${LANG_NAMES[session.target_language]} → ${LANG_NAMES[session.source_language]}`}
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    {textDirection === 'forward'
                      ? `${(LANG_NAMES[session.source_language] || session.source_language).slice(0, 3)}→${(LANG_NAMES[session.target_language] || session.target_language).slice(0, 3)}`
                      : `${(LANG_NAMES[session.target_language] || session.target_language).slice(0, 3)}→${(LANG_NAMES[session.source_language] || session.source_language).slice(0, 3)}`
                    }
                  </Button>
                  <Input
                    value={inputText}
                    onChange={e => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Or type text to translate..."
                    className="flex-1"
                    data-testid="translate-input"
                    disabled={translating}
                  />
                  <Button
                    onClick={handleTranslate}
                    disabled={!inputText.trim() || translating}
                    className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white"
                    data-testid="send-translation-button"
                  >
                    {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Transcript - newest at top */}
            <ScrollArea className="flex-1 p-6" ref={scrollRef}>
              {transcript.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <Mic className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p className="text-lg">Start speaking or type text to translate</p>
                    <p className="text-sm mt-1">Use the microphone above to record, or type in the text field</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 pb-4">
                  {[...transcript].reverse().map((entry, i) => (
                    <div key={entry.id || i} className="animate-fade-in" data-testid={`transcript-entry-${i}`}>
                      {/* Original */}
                      <div className="flex justify-end mb-2">
                        <div className="max-w-[80%] bg-[hsl(var(--brand))]/10 border border-[hsl(var(--brand))]/20 rounded-2xl rounded-br-md px-4 py-3">
                          <p className="text-sm">{entry.original}</p>
                          <p className="text-xs text-muted-foreground mt-1">{LANG_NAMES[entry.source_language] || 'EN'}</p>
                        </div>
                      </div>
                      {/* Translation */}
                      {showTranscript && entry.translated && (
                        <div className="flex justify-start mb-2">
                          <div className="max-w-[80%] bg-secondary rounded-2xl rounded-bl-md px-4 py-3 group">
                            <p className="text-sm">{entry.translated}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-muted-foreground">{LANG_NAMES[entry.target_language]}</p>
                              <Badge variant={entry.advisory_level === 'NONE' ? 'secondary' : entry.advisory_level === 'ADVISORY' ? 'outline' : 'destructive'} className="text-[10px] px-1.5 py-0">
                                {(entry.confidence * 100).toFixed(0)}%
                              </Badge>
                              <Button
                                variant="ghost" size="icon"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => playTTS(entry.translated, entry.target_language)}
                                data-testid={`play-tts-${i}`}
                              >
                                <Volume2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Controls Sidebar */}
          <div className="flex flex-col p-6 gap-4 bg-card/50">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Session Controls</h3>

            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <Badge>{session.status}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Microphone</span>
                {micPermission === 'granted' && <Badge className="bg-green-500/10 text-green-500 border-green-500/20" data-testid="mic-status-granted"><Mic className="w-3 h-3 mr-1" />Enabled</Badge>}
                {micPermission === 'denied' && <Badge variant="destructive" data-testid="mic-status-denied"><MicOff className="w-3 h-3 mr-1" />Blocked</Badge>}
                {micPermission === 'prompt' && <Badge variant="outline" data-testid="mic-status-prompt"><Mic className="w-3 h-3 mr-1" />Not Set</Badge>}
                {micPermission === 'unknown' && <Badge variant="outline" data-testid="mic-status-unknown"><Mic className="w-3 h-3 mr-1" />Unknown</Badge>}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Advisory</span>
                <Badge variant={session.advisory_state === 'NONE' ? 'secondary' : session.advisory_state === 'ADVISORY' ? 'outline' : 'destructive'}>
                  {session.advisory_state}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">AI Seconds</span>
                <span className="font-mono text-xs">{session.ai_seconds}s</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Messages</span>
                <span className="font-mono text-xs">{transcript.length}</span>
              </div>
            </div>

            {!isEnded && (
              <>
                <Button variant="outline" onClick={handleEscalate} disabled={session.escalated}
                  className="w-full border-[hsl(var(--warning))]/50 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10"
                  data-testid="escalate-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  {session.escalated ? 'Escalated' : 'Request Human'}
                </Button>
                <Button variant="destructive" onClick={() => setShowEndDialog(true)} className="w-full" data-testid="end-session-button">
                  <PhoneOff className="w-4 h-4 mr-2" />End Session
                </Button>
              </>
            )}

            {isEnded && endSummary && (
              <div className="rounded-lg border p-4 bg-secondary/50">
                <h4 className="font-semibold text-sm mb-3" style={{ fontFamily: 'DM Sans' }}>Session Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">AI Seconds</span><span className="font-mono">{endSummary.ai_seconds}s</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Human Minutes</span><span className="font-mono">{endSummary.human_minutes}min</span></div>
                  <div className="flex justify-between font-medium"><span>Total Cost</span><span className="font-mono">${(endSummary.total_cost_cents / 100).toFixed(2)}</span></div>
                </div>
              </div>
            )}

            {/* Audio Playback Status */}
            {playingAudio && (
              <div className="rounded-lg border p-3 bg-[hsl(var(--brand))]/5 flex items-center gap-2" data-testid="audio-playing-indicator">
                <Volume2 className="w-4 h-4 text-[hsl(var(--brand))] animate-pulse" />
                <span className="text-xs text-[hsl(var(--brand))]">Playing audio...</span>
              </div>
            )}
          </div>
        </div>

        {/* End Session Dialog */}
        <Dialog open={showEndDialog} onOpenChange={setShowEndDialog}>
          <DialogContent data-testid="end-session-dialog">
            <DialogHeader>
              <DialogTitle>End Session</DialogTitle>
              <DialogDescription>Are you sure you want to end this interpretation session?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEndDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleEndSession} data-testid="confirm-end-session">End Session</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* High Risk Dialog */}
        <Dialog open={showRiskDialog} onOpenChange={() => {}}>
          <DialogContent data-testid="high-risk-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive"><Shield className="w-5 h-5" />High Risk Translation</DialogTitle>
              <DialogDescription>The AI translation confidence is critically low. Do you acknowledge this risk and wish to continue?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowRiskDialog(false); handleEscalate(); }}>Request Human Instead</Button>
              <Button variant="destructive" onClick={handleAcknowledgeRisk} data-testid="acknowledge-risk-button">I Acknowledge &amp; Continue</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rate Dialog */}
        <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
          <DialogContent data-testid="rate-dialog">
            <DialogHeader>
              <DialogTitle>Rate Your Session</DialogTitle>
              <DialogDescription>Help us improve by rating your experience.</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 py-4">
              <div>
                <Label className="mb-2 block">AI Interpretation</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Button key={n} variant="ghost" size="icon" onClick={() => setAiRating(n)} data-testid={`ai-rating-${n}`}>
                      <Star className={`w-6 h-6 ${n <= aiRating ? 'fill-[hsl(var(--warning))] text-[hsl(var(--warning))]' : 'text-muted-foreground'}`} />
                    </Button>
                  ))}
                </div>
              </div>
              {session.human_interpreter_id && (
                <div>
                  <Label className="mb-2 block">Human Interpreter</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Button key={n} variant="ghost" size="icon" onClick={() => setHumanRating(n)} data-testid={`human-rating-${n}`}>
                        <Star className={`w-6 h-6 ${n <= humanRating ? 'fill-[hsl(var(--warning))] text-[hsl(var(--warning))]' : 'text-muted-foreground'}`} />
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="mb-2 block">Feedback (optional)</Label>
                <Input value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Any additional feedback..." data-testid="feedback-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => navigate('/client')}>Skip</Button>
              <Button onClick={handleRate} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-rating-button">Submit Rating</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
